import {
  ShieldCheck, Bot, Users, Star, CheckCircle, FileText,
  Lock, Scale, Award, Clock, History, Trash2, Eye,
} from 'lucide-react'
import { useLang } from '../i18n/LangContext'

// ============ Review process steps ============
interface ReviewStep {
  num: number
  icon: typeof Bot
  title: { zh: string; en: string }
  desc: { zh: string; en: string }
  time?: { zh: string; en: string }
  grades?: string[]
}

const reviewSteps: ReviewStep[] = [
  {
    num: 1,
    icon: Bot,
    title: { zh: '自动初审', en: 'Auto Screening' },
    desc: {
      zh: '系统自动检查格式完整性、约束一致性、工具可用性',
      en: 'System auto-checks format completeness, constraint consistency, tool availability',
    },
    time: { zh: '即时', en: 'Instant' },
  },
  {
    num: 2,
    icon: Users,
    title: { zh: '专家评审', en: 'Expert Review' },
    desc: {
      zh: '同行专家评审任务质量、参考答案准确性、评分标准合理性',
      en: 'Peer experts review task quality, reference answer accuracy, rubric fairness',
    },
    time: { zh: '3-5个工作日', en: '3-5 business days' },
  },
  {
    num: 3,
    icon: Star,
    title: { zh: '质量评级', en: 'Quality Grading' },
    desc: {
      zh: '根据复杂度、代表性、可验证性评定质量等级',
      en: 'Graded by complexity, representativeness, verifiability',
    },
    grades: ['A+', 'A', 'B+', 'B', 'C'],
  },
  {
    num: 4,
    icon: CheckCircle,
    title: { zh: '发布与署名', en: 'Publication & Attribution' },
    desc: {
      zh: '通过审核后发布，记录贡献者署名',
      en: 'Published after review, with contributor attribution',
    },
  },
]

// ============ Quality grade rows ============
interface GradeRow {
  grade: string
  standard: { zh: string; en: string }
  usage: { zh: string; en: string }
}

const gradeRows: GradeRow[] = [
  {
    grade: 'A+',
    standard: {
      zh: '复杂度L6+，含完整参考答案和评分标准，可验证',
      en: 'Complexity L6+, with full reference answer and rubric, verifiable',
    },
    usage: { zh: '评测集+训练集', en: 'Eval set + Train set' },
  },
  {
    grade: 'A',
    standard: {
      zh: '复杂度L4+，含参考答案，可验证',
      en: 'Complexity L4+, with reference answer, verifiable',
    },
    usage: { zh: '评测集+训练集', en: 'Eval set + Train set' },
  },
  {
    grade: 'B+',
    standard: {
      zh: '复杂度L3+，有基本评分标准',
      en: 'Complexity L3+, with basic rubric',
    },
    usage: { zh: '评测集', en: 'Eval set' },
  },
  {
    grade: 'B',
    standard: {
      zh: '复杂度L1+，基本完整',
      en: 'Complexity L1+, basically complete',
    },
    usage: { zh: '评测集（需改进）', en: 'Eval set (needs improvement)' },
  },
  {
    grade: 'C',
    standard: { zh: '不满足要求', en: 'Does not meet requirements' },
    usage: { zh: '不采纳', en: 'Rejected' },
  },
]

// ============ Version management points ============
const versionPoints: { zh: string; en: string }[] = [
  { zh: '每个 Task 有版本号 (v1, v2, v3...)', en: 'Each Task has a version number (v1, v2, v3...)' },
  { zh: '修改评分标准时自动创建新版本', en: 'New version created automatically when rubric changes' },
  { zh: '历史版本可追溯', en: 'Historical versions are traceable' },
  { zh: '评测记录绑定具体版本', en: 'Evaluation records are bound to specific versions' },
]

// ============ Privacy & licensing cards ============
interface InfoCard {
  icon: typeof Lock
  title: { zh: string; en: string }
  items: { zh: string; en: string }[]
}

const privacyCards: InfoCard[] = [
  {
    icon: Lock,
    title: { zh: '隐私脱敏', en: 'Privacy Anonymization' },
    items: [
      { zh: 'Agent 身份信息去除', en: 'Agent identity information removed' },
      { zh: '个人信息脱敏', en: 'Personal information anonymized' },
      { zh: '敏感数据过滤', en: 'Sensitive data filtered' },
      { zh: '遵循 GDPR / PIPL 标准', en: 'Follows GDPR / PIPL standards' },
    ],
  },
  {
    icon: Scale,
    title: { zh: '授权协议', en: 'Licensing' },
    items: [
      { zh: 'CC-BY-4.0（默认，推荐）', en: 'CC-BY-4.0 (default, recommended)' },
      { zh: 'CC-BY-SA-4.0（署名-相同方式共享）', en: 'CC-BY-SA-4.0 (Share-Alike)' },
      { zh: 'CC-BY-NC-4.0（署名-非商业）', en: 'CC-BY-NC-4.0 (Non-Commercial)' },
      { zh: '保留所有权利', en: 'All rights reserved' },
    ],
  },
  {
    icon: Award,
    title: { zh: '贡献者权益', en: 'Contributor Rights' },
    items: [
      { zh: '署名权（永久）', en: 'Attribution right (permanent)' },
      { zh: '撤回权（发布前可撤回）', en: 'Withdrawal right (before publication)' },
      { zh: '修改权（通过审核后可提交新版本）', en: 'Modification right (new version after review)' },
      { zh: '知情权（被告知数据使用情况）', en: 'Right to be informed (data usage)' },
      { zh: '奖励权（高质量贡献获得奖金）', en: 'Reward right (bonuses for high-quality contributions)' },
    ],
  },
]

// ============ Data retention policy ============
const retentionPoints: { zh: string; en: string }[] = [
  { zh: '评测数据保留：永久（用于历史对比）', en: 'Evaluation data: permanent (for historical comparison)' },
  { zh: '训练数据保留：按授权期限', en: 'Training data: per license term' },
  { zh: '个人信息：任务发布后脱敏', en: 'Personal information: anonymized after publication' },
  { zh: '删除请求：可申请删除未发布的任务', en: 'Deletion request: unpublished tasks can be deleted on request' },
]

// Grade color helper
function gradeColor(grade: string): string {
  switch (grade) {
    case 'A+': return 'var(--color-success)'
    case 'A': return 'rgba(74,222,128,0.85)'
    case 'B+': return 'var(--color-info)'
    case 'B': return 'var(--color-warning)'
    case 'C': return 'var(--color-danger)'
    default: return 'var(--color-text-secondary)'
  }
}

export default function DataGovernancePage() {
  const { lang } = useLang()
  const isZh = lang === 'zh'

  return (
    <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
      {/* ============ 1. Header ============ */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-11 h-11 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-accent-glow)' }}
          >
            <ShieldCheck size={22} style={{ color: 'var(--color-accent)' }} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
              {isZh ? '数据治理' : 'Data Governance'}
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              {isZh
                ? '审核流程、质量标准、版本管理与隐私保护'
                : 'Review process, quality standards, version management & privacy protection'}
            </p>
          </div>
        </div>
      </section>

      {/* ============ 2. Review Process (vertical timeline) ============ */}
      <section className="mb-12">
        <div className="flex items-center gap-2 mb-6">
          <CheckCircle size={18} style={{ color: 'var(--color-accent)' }} />
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {isZh ? '审核流程' : 'Review Process'}
          </h2>
        </div>

        <div className="relative">
          {/* Vertical center line: left on mobile, centered on desktop */}
          <div
            className="absolute top-2 bottom-2 w-px left-6 md:left-1/2 md:-translate-x-px"
            style={{ backgroundColor: 'var(--color-border)' }}
          />

          <div className="space-y-8 md:space-y-0">
            {reviewSteps.map((step, idx) => {
              const Icon = step.icon
              const isLeft = idx % 2 === 0
              return (
                <div key={step.num} className="relative md:mb-12 md:last:mb-0">
                  {/* Dot with icon */}
                  <div
                    className="absolute left-6 md:left-1/2 -translate-x-1/2 top-1 w-11 h-11 rounded-full flex items-center justify-center z-10"
                    style={{
                      backgroundColor: 'var(--color-bg-primary)',
                      border: '2px solid var(--color-accent)',
                      boxShadow: '0 0 0 4px var(--color-bg-primary)',
                    }}
                  >
                    <Icon size={18} style={{ color: 'var(--color-accent)' }} />
                  </div>

                  {/* Content card — alternating sides on desktop, all-left on mobile */}
                  <div
                    className={`pl-16 md:w-[calc(50%-3rem)] ${
                      isLeft
                        ? 'md:mr-auto md:pl-0 md:pr-2'
                        : 'md:ml-auto md:pl-2 md:pr-0'
                    }`}
                  >
                    <div
                      className="rounded-xl border p-5"
                      style={{
                        backgroundColor: 'var(--color-bg-card)',
                        borderColor: 'var(--color-border)',
                      }}
                    >
                      {/* Step number + title */}
                      <div className="flex items-center gap-2.5 mb-2">
                        <span
                          className="text-xs font-bold px-2 py-0.5 rounded-md shrink-0"
                          style={{
                            backgroundColor: 'var(--color-accent-glow)',
                            color: 'var(--color-accent)',
                          }}
                        >
                          {isZh ? `步骤 ${step.num}` : `Step ${step.num}`}
                        </span>
                        <h3 className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                          {step.title[lang]}
                        </h3>
                      </div>

                      {/* Description */}
                      <p className="text-xs leading-relaxed mb-3" style={{ color: 'var(--color-text-secondary)' }}>
                        {step.desc[lang]}
                      </p>

                      {/* Time / Grades metadata */}
                      {step.time && (
                        <div className="flex items-center gap-1.5">
                          <Clock size={12} style={{ color: 'var(--color-text-muted)' }} />
                          <span className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                            {step.time[lang]}
                          </span>
                        </div>
                      )}
                      {step.grades && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Star size={12} style={{ color: 'var(--color-text-muted)' }} />
                          {step.grades.map(g => (
                            <span
                              key={g}
                              className="text-[11px] font-bold px-1.5 py-0.5 rounded"
                              style={{
                                backgroundColor: `${gradeColor(g)}1f`,
                                color: gradeColor(g),
                              }}
                            >
                              {g}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ============ 3. Quality Grades Table ============ */}
      <section className="mb-12">
        <div className="flex items-center gap-2 mb-6">
          <Star size={18} style={{ color: 'var(--color-accent)' }} />
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {isZh ? '质量等级标准' : 'Quality Grades'}
          </h2>
        </div>

        <div
          className="rounded-xl border overflow-hidden"
          style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                  <th
                    className="text-left px-4 py-3 font-semibold whitespace-nowrap"
                    style={{ color: 'var(--color-text-primary)', borderBottom: '1px solid var(--color-border)' }}
                  >
                    {isZh ? '等级' : 'Grade'}
                  </th>
                  <th
                    className="text-left px-4 py-3 font-semibold"
                    style={{ color: 'var(--color-text-primary)', borderBottom: '1px solid var(--color-border)' }}
                  >
                    {isZh ? '标准' : 'Standard'}
                  </th>
                  <th
                    className="text-left px-4 py-3 font-semibold whitespace-nowrap"
                    style={{ color: 'var(--color-text-primary)', borderBottom: '1px solid var(--color-border)' }}
                  >
                    {isZh ? '数据用途' : 'Data Usage'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {gradeRows.map((row, idx) => (
                  <tr
                    key={row.grade}
                    style={{
                      backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(36,39,54,0.4)',
                      borderTop: '1px solid var(--color-border)',
                    }}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className="inline-flex items-center justify-center min-w-[2rem] text-sm font-bold px-2 py-1 rounded-md"
                        style={{
                          backgroundColor: `${gradeColor(row.grade)}1f`,
                          color: gradeColor(row.grade),
                        }}
                      >
                        {row.grade}
                      </span>
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--color-text-secondary)' }}>
                      {row.standard[lang]}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" style={{ color: 'var(--color-text-secondary)' }}>
                      {row.usage[lang]}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ============ 4. Version Management ============ */}
      <section className="mb-12">
        <div className="flex items-center gap-2 mb-6">
          <History size={18} style={{ color: 'var(--color-accent)' }} />
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {isZh ? '版本管理' : 'Version Management'}
          </h2>
        </div>

        <div
          className="rounded-xl border p-6"
          style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
        >
          <div className="flex items-start gap-2 mb-5">
            <FileText size={14} className="mt-0.5 shrink-0" style={{ color: 'var(--color-info)' }} />
            <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
              {isZh
                ? '所有任务变更均通过版本号管理，确保评测的可追溯性与可复现性。'
                : 'All task changes are managed through version numbers to ensure traceability and reproducibility.'}
            </p>
          </div>
          <ul className="space-y-3">
            {versionPoints.map((point, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  style={{ backgroundColor: 'var(--color-accent-glow)' }}
                >
                  <span className="text-[10px] font-bold" style={{ color: 'var(--color-accent)' }}>
                    {idx + 1}
                  </span>
                </span>
                <span className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                  {point[lang]}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ 5. Privacy & Licensing ============ */}
      <section className="mb-12">
        <div className="flex items-center gap-2 mb-6">
          <Lock size={18} style={{ color: 'var(--color-accent)' }} />
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {isZh ? '隐私与授权' : 'Privacy & Licensing'}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {privacyCards.map((card, idx) => {
            const Icon = card.icon
            return (
              <div
                key={idx}
                className="rounded-xl border p-5 flex flex-col"
                style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
              >
                {/* Card header */}
                <div className="flex items-center gap-2.5 mb-4 pb-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: 'var(--color-accent-glow)' }}
                  >
                    <Icon size={18} style={{ color: 'var(--color-accent)' }} />
                  </div>
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {card.title[lang]}
                  </h3>
                </div>
                {/* Items */}
                <ul className="space-y-2.5">
                  {card.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle
                        size={13}
                        className="mt-0.5 shrink-0"
                        style={{ color: 'var(--color-success)' }}
                      />
                      <span className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                        {item[lang]}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      </section>

      {/* ============ 6. Data Retention Policy ============ */}
      <section className="mb-4">
        <div className="flex items-center gap-2 mb-6">
          <Trash2 size={18} style={{ color: 'var(--color-accent)' }} />
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {isZh ? '数据保留策略' : 'Data Retention Policy'}
          </h2>
        </div>

        <div
          className="rounded-xl border p-6"
          style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {retentionPoints.map((point, idx) => {
              const icons = [History, Clock, Eye, Trash2]
              const Icon = icons[idx] ?? Eye
              return (
                <div
                  key={idx}
                  className="flex items-start gap-3 p-3 rounded-lg"
                  style={{
                    backgroundColor: 'var(--color-bg-tertiary)',
                    border: '1px solid var(--color-border)',
                  }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: 'var(--color-accent-glow)' }}
                  >
                    <Icon size={15} style={{ color: 'var(--color-accent)' }} />
                  </div>
                  <span className="text-xs leading-relaxed pt-1" style={{ color: 'var(--color-text-secondary)' }}>
                    {point[lang]}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </section>
    </div>
  )
}
